/**
 * Datart
 *
 * Copyright 2021
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  AggregateFieldActionType,
  ChartDataSectionType,
  ChartDataViewFieldCategory,
  DataViewFieldType,
  FilterConditionType,
  SortActionType,
} from 'app/constants';
import {
  ChartDataConfig,
  ChartDataSectionField,
  RelationFilterValue,
} from 'app/types/ChartConfig';
import { ChartStyleConfigDTO } from 'app/types/ChartConfigDTO';
import {
  ChartDataRequest,
  ChartDataRequestFilter,
  PendingChartDataRequestFilter,
} from 'app/types/ChartDataRequest';
import { ChartDatasetPageInfo } from 'app/types/ChartDataSet';
import ChartDataView from 'app/types/ChartDataView';
import { IChartDrillOption } from 'app/types/ChartDrillOption';
import { findPathByNameInMeta, getAllColumnInMeta, getRuntimeDateLevelFields, getValue } from 'app/utils/chartHelper';
import { transformToViewConfig } from 'app/utils/internalChartHelper';
import {
  formatTime,
  getTime,
  recommendTimeRangeConverter,
  splitRangerDateFilters,
} from 'app/utils/time';
import { FilterSqlOperator, RUNTIME_FILTER_KEY, TIME_FORMATTER } from 'globalConstants';
import { isEmptyArray, isEqualObject, IsKeyIn, UniqWith } from 'utils/object';
import { DrillMode } from './ChartDrillOption';
import { ChartDataViewMeta } from 'app/types/ChartDataViewMeta';
export class ChartDataRequestBuilder {
  extraSorters: ChartDataRequest['orders'] = [];
  extraRuntimeFilters: ChartDataRequestFilter[] = [];
  chartDataConfigs: ChartDataConfig[];
  charSettingConfigs;
  pageInfo;
  dataView;
  script: boolean;
  aggregation?: boolean;
  drillOption?: IChartDrillOption;
  variableParams?: Record<string, any[]>;
  sumColumns?;

  constructor(
    dataView: Pick<ChartDataView, 'id' | 'computedFields' | 'meta' | 'type'> & {
      config: string | object;
    },
    dataConfigs?: ChartDataConfig[],
    settingConfigs?: ChartStyleConfigDTO[],
    pageInfo?: ChartDatasetPageInfo,
    script?: boolean,
    aggregation?: boolean,
    sumFieldConfigs?: ChartDataSectionField[],
  ) {
    this.dataView = dataView;
    this.chartDataConfigs = dataConfigs || [];
    this.charSettingConfigs = settingConfigs || [];
    this.pageInfo = pageInfo || {};
    this.script = script || false;
    this.aggregation = aggregation;
    this.sumColumns = sumFieldConfigs?.map(config => {
      if (aggregation) {
        return config.aggregate + '(' + config.colName + ')'
      } else {
        return config.colName
      }
    });
  }

  public addExtraSorters(sorters: ChartDataRequest['orders']) {
    if (!isEmptyArray(sorters)) {
      this.extraSorters = this.extraSorters.concat(sorters!);
    }
    return this;
  }

  public addDrillOption(drillOption?: IChartDrillOption) {
    this.drillOption = drillOption;
    return this;
  }

  public addRuntimeFilters(filters: PendingChartDataRequestFilter[] = []) {
    if (!isEmptyArray(filters)) {
      this.extraRuntimeFilters = filters.map(v => {
        return {
          ...v,
          column: this.buildColumnName({ v, colName: v.column }),
        } as unknown as ChartDataRequestFilter;
      });
    }
    return this;
  }

  public addVariableParams(params?: Record<string, any[]>) {
    if (params) {
      this.variableParams = params;
    }
    return this;
  }

  private buildAggregators() {
    if (this.aggregation === false) {
      return [];
    }

    const aggColumns = this.chartDataConfigs.reduce<ChartDataSectionField[]>(
      (acc, cur) => {
        if (!cur.rows) {
          return acc;
        }
        if (
          cur.type === ChartDataSectionType.AGGREGATE ||
          cur.type === ChartDataSectionType.SIZE ||
          cur.type === ChartDataSectionType.INFO
        ) {
          return acc.concat(cur.rows);
        }

        if (
          cur.type === ChartDataSectionType.MIXED &&
          cur.rows?.findIndex(v => v.type === DataViewFieldType.NUMERIC) !== -1
        ) {
          return acc.concat(
            cur.rows.filter(v => v.type === DataViewFieldType.NUMERIC),
          );
        }
        return acc;
      },
      [],
    );

    return UniqWith(
      aggColumns.map(aggCol => ({
        column: aggCol.colName,
        sqlOperator: aggCol.aggregate!,
      })),
      (a, b) => a.column === b.column && a.sqlOperator === b.sqlOperator,
    );
  }

  private buildGroups() {
    /**
     * If aggregation is off, do not add values to gruop
     */
    if (this.aggregation === false) {
      return [];
    }
    const groupColumns = this.chartDataConfigs.reduce<ChartDataSectionField[]>(
      (acc, cur) => {
        if (isEmptyArray(cur.rows)) {
          return acc;
        }
        if (cur.type === ChartDataSectionType.COLOR) {
          return acc.concat(cur.rows || []);
        }
        if (cur.type === ChartDataSectionType.GROUP) {
          const rows = getRuntimeDateLevelFields(cur.rows);

          if (cur.drillable) {
            if (
              !this.drillOption ||
              this.drillOption?.mode === DrillMode.Normal ||
              !this.drillOption?.getCurrentFields()
            ) {
              return acc.concat(rows?.[0] || []);
            }
            return acc.concat(
              rows?.filter(field => {
                return Boolean(
                  this.drillOption
                    ?.getCurrentFields()
                    ?.some(df => df.uid === field.uid),
                );
              }) || [],
            );
          }
          return acc.concat(rows || []);
        }
        if (cur.type === ChartDataSectionType.MIXED) {
          const dateAndStringFields = cur.rows?.filter(v =>
            [DataViewFieldType.DATE, DataViewFieldType.STRING].includes(v.type),
          );
          //zh: 判断数据中是否含有 DATE 和 STRING 类型 en: Determine whether the data contains DATE and STRING types
          return acc.concat(dateAndStringFields || []);
        }
        return acc;
      },
      [],
    );
    return Array.from(
      new Set(groupColumns.map(groupCol => ({ column: groupCol.colName }))),
    );
  }

  private buildFilters(): ChartDataRequestFilter[] {
    const fields: ChartDataSectionField[] = this.chartDataConfigs
      .reduce<ChartDataSectionField[]>((acc, cur) => {
        if (!cur.rows || cur.type !== ChartDataSectionType.FILTER) {
          return acc;
        }
        return acc.concat(cur.rows);
      }, [])
      .filter(col => Boolean(col.filter?.condition))
      .filter(col => {
        if (
          col.filter?.condition?.operator === FilterSqlOperator.Null ||
          col.filter?.condition?.operator === FilterSqlOperator.NotNull
        ) {
          return true;
        } else if (Array.isArray(col.filter?.condition?.value)) {
          return Boolean(col.filter?.condition?.value?.length);
        }
        return true;
      })
      .map(col => col);
    return this.normalizeFilters(fields).concat(this.normalizeDrillFilters());
  }

  private normalizeFilters = (fields: ChartDataSectionField[]) => {
    const _timeConverter = (visualType, value) => {
      if (visualType !== 'DATE') {
        return value;
      }
      if (Boolean(value) && typeof value === 'object' && 'unit' in value) {
        const time = getTime(+(value.direction + value.amount), value.unit)(
          value.unit,
          value.isStart,
        );
        return formatTime(time, TIME_FORMATTER);
      }
      return formatTime(value, TIME_FORMATTER);
    };

    const _transformFieldValues = (field: ChartDataSectionField) => {
      const conditionValue = field.filter?.condition?.value;
      if (!conditionValue) {
        return null;
      }
      if (Array.isArray(conditionValue)) {
        return conditionValue
          .map(v => {
            if (IsKeyIn(v as RelationFilterValue, 'key')) {
              const listItem = v as RelationFilterValue;
              if (!listItem.isSelected) {
                return undefined;
              }
              return {
                value: listItem.key,
                valueType: field.type,
              };
            } else {
              return {
                value: _timeConverter(field.filter?.condition?.visualType, v),
                valueType: field.type,
              };
            }
          })
          .filter(Boolean) as any[];
      }
      if (
        field?.filter?.condition?.type === FilterConditionType.RecommendTime
      ) {
        const timeRange = recommendTimeRangeConverter(conditionValue);
        return timeRange.map(t => ({
          value: t,
          valueType: field.type,
        }));
      }
      return [
        {
          value: _timeConverter(
            field.filter?.condition?.visualType,
            conditionValue,
          ),
          valueType: field.type,
        },
      ];
    };
    const filters = fields
      .map(field => {
        if (
          field.filter?.condition?.operator === FilterSqlOperator.In ||
          field.filter?.condition?.operator === FilterSqlOperator.NotIn
        ) {
          if (isEmptyArray(_transformFieldValues(field))) {
            return null;
          }
        }
        return {
          aggOperator:
            field.aggregate === AggregateFieldActionType.NONE
              ? null
              : field.aggregate,
          column: field.colName,
          sqlOperator: field.filter?.condition?.operator!,
          values: _transformFieldValues(field) || [],
        };
      })
      .filter(Boolean) as ChartDataRequestFilter[];
    return splitRangerDateFilters(filters);
  };

  private normalizeDrillFilters(): ChartDataRequestFilter[] {
    return (this.drillOption
      ?.getAllDrillDownFields()
      .filter(field => Boolean(field.condition))
      .map(f => {
        return {
          aggOperator: null,
          column: f.condition?.name!,
          sqlOperator: f.condition?.operator! as FilterSqlOperator,
          values: [
            { value: f.condition?.value as string, valueType: 'STRING' },
          ],
        };
      }) || []) as ChartDataRequestFilter[];
  }

  private normalizeRuntimeFilters(): ChartDataRequestFilter[] {
    return (this.chartDataConfigs || [])
      .filter(c => c.type === ChartDataSectionType.FILTER)
      .flatMap(c => {
        return c[RUNTIME_FILTER_KEY] || [];
      })
      .map(v => {
        return {
          ...v,
          column: this.buildColumnName({ colName: v.column }),
        };
      })
      .concat(this.extraRuntimeFilters);
  }

  private buildOrders() {
    const sortColumns = this.chartDataConfigs
      .reduce<ChartDataSectionField[]>((acc, cur) => {
        if (!cur.rows) {
          return acc;
        }
        if (
          cur.type === ChartDataSectionType.GROUP ||
          cur.type === ChartDataSectionType.AGGREGATE ||
          cur.type === ChartDataSectionType.MIXED
        ) {
          return acc.concat(cur.rows);
        }
        return acc;
      }, [])
      .filter(
        col =>
          col?.sort?.type &&
          [SortActionType.ASC, SortActionType.DESC].includes(col?.sort?.type),
      );

    const originalSorters = sortColumns.map(aggCol => ({
      column: aggCol.colName,
      operator: aggCol.sort?.type!,
      aggOperator: aggCol.aggregate,
    }));

    return originalSorters
      .reduce<ChartDataRequest['orders']>((acc, cur) => {
        const uniqSorter = sorter =>
          `${sorter.column}-${
            sorter.aggOperator?.length > 0 ? sorter.aggOperator : ''
          }`;
        const newSorter = this.extraSorters?.find(
          extraSorter => uniqSorter(extraSorter) === uniqSorter(cur),
        );
        if (newSorter) {
          return acc;
        }
        return acc.concat([cur]);
      }, [])
      .concat(this.extraSorters as [])
      .filter(sorter => Boolean(sorter?.operator));
  }

  private buildPageInfo() {
    const settingStyles = this.charSettingConfigs;
    const pageSize = getValue(settingStyles, ['paging', 'pageSize']);
    const enablePaging = getValue(settingStyles, ['paging', 'enablePaging']);
    return {
      countTotal: !!enablePaging,
      pageNo: this.pageInfo?.pageNo,
      pageSize: pageSize || 1000,
    };
  }

  private buildFunctionColumns() {
    const _removeSquareBrackets = expression => {
      if (!expression) {
        return '';
      }
      return expression.replaceAll('[', '').replaceAll(']', '');
    };

    return (this.dataView.computedFields || []).map(f => ({
      alias: f.id!,
      snippet: _removeSquareBrackets(f.expression),
    }));
  }

  private buildSelectColumns() {
    const selectColumns = this.chartDataConfigs.reduce<ChartDataSectionField[]>(
      (acc, cur) => {
        if (!cur.rows) {
          return acc;
        }

        if (this.aggregation === false) {
          if (
            cur.type === ChartDataSectionType.COLOR ||
            cur.type === ChartDataSectionType.AGGREGATE ||
            cur.type === ChartDataSectionType.SIZE ||
            cur.type === ChartDataSectionType.INFO ||
            cur.type === ChartDataSectionType.MIXED
          ) {
            return acc.concat(cur.rows);
          } else if (cur.type === ChartDataSectionType.GROUP) {
            if (cur.drillable) {
              if (
                !this.drillOption ||
                this.drillOption?.mode === DrillMode.Normal ||
                !this.drillOption?.getCurrentFields()
              ) {
                return acc.concat(cur.rows?.[0] || []);
              }
              return acc.concat(
                cur.rows?.filter(field => {
                  return Boolean(
                    this.drillOption
                      ?.getCurrentFields()
                      ?.some(df => df.uid === field.uid),
                  );
                }) || [],
              );
            } else {
              return acc.concat(cur.rows);
            }
          }
        }

        return acc;
      },
      [],
    );
    return selectColumns.map(col => col.colName);
  }

  private buildViewConfigs() {
    return transformToViewConfig(this.dataView?.config);
  }

  public build(): ChartDataRequest {
    return {
      ...this.buildViewConfigs(),
      viewId: this.dataView?.id,
      aggregators: this.buildAggregators(),
      groups: this.buildGroups(),
      filters: this.buildFilters(),
      orders: this.buildOrders(),
      pageInfo: this.buildPageInfo(),
      functionColumns: this.buildFunctionColumns(),
      columns: this.buildSelectColumns(),
      script: this.script,
      sumColumns: this.sumColumns
    };
  }

  // private removeInvalidFilter(filters: ChartDataRequestFilter[]) {
  //   const dataViewFieldsNames = (
  //     getAllColumnInMeta(this.dataView?.meta) as ChartDataViewMeta[]
  //   )
  //     .concat(this.dataView?.computedFields || [])
  //     .map(c => c?.name);

  //   return (filters || []).filter(f => {
  //     return dataViewFieldsNames.includes(f.column.join('.'));
  //   });
  // }

  private isInValidDrillOption() {
    return (
      !this.drillOption ||
      this.drillOption?.mode === DrillMode.Normal ||
      !this.drillOption?.getCurrentFields()
    );
  }

  private buildDetailColumns() {
    const selectColumns = this.chartDataConfigs.reduce<ChartDataSectionField[]>(
      (acc, cur) => {
        if (!cur.rows) {
          return acc;
        }
        if (cur.drillable) {
          if (this.isInValidDrillOption()) {
            return acc.concat(cur.rows?.[0] || []);
          }
          return acc.concat(
            cur.rows?.filter(field => {
              return Boolean(
                this.drillOption
                  ?.getCurrentFields()
                  ?.some(df => df.uid === field.uid),
              );
            }) || [],
          );
        } else {
          return acc.concat(cur.rows);
        }
      },
      [],
    );

    return selectColumns
      ?.filter(
        c => c.category !== ChartDataViewFieldCategory.AggregateComputedField,
      )
      ?.reduce<ChartDataSectionField[]>((acc, cur) => {
        if (acc.find(x => x?.colName === cur.colName)) {
          return acc;
        }
        return acc.concat(cur);
      }, [])
      ?.map(col => {
        return {
          alias: this.buildAliasName(col),
          column: this.buildColumnName(col),
        };
      });
  }

  private buildColumnName(col) {
    const row = findPathByNameInMeta(this.dataView.meta, col.colName);
    if (row) {
      return row?.path || [];
    } else {
      return [col.colName];
    }
  }

  private buildAliasName(c) {
    if (c.aggregate === AggregateFieldActionType.NONE) {
      return c.colName;
    }
    if (c.aggregate) {
      return `${c.aggregate}(${c.colName})`;
    }
    return c.colName;
  }

  // public buildDetails(): ChartDataRequest {
  //   const validFilters = this.removeInvalidFilter(
  //     this.buildFilters().filter(f => !f.aggOperator),
  //   );
  //   return {
  //     ...this.buildViewConfigs(),
  //     viewId: this.dataView?.id,
  //     aggregators: [],
  //     groups: [],
  //     filters: validFilters,
  //     orders: [],
  //     pageInfo: this.buildPageInfo(),
  //     functionColumns: this.buildFunctionColumns(),
  //     columns: this.buildDetailColumns(),
  //     script: this.script,
  //     params: this.variableParams,
  //   };
  // }


  public getColNameStringFilter(): PendingChartDataRequestFilter[] {
    return this.buildFilters().map(v => {
      const row = getAllColumnInMeta(this.dataView.meta)?.find(val =>
        isEqualObject(val.path, v.column),
      );
      return {
        ...v,
        column: row?.name || '',
      };
    });
  }
}
